/**
 * InterjectionSlice — human-in-the-loop steering channel state.
 *
 * Stores the chronological list of `UserInterjection` records that the
 * user has submitted into the live debate. Records are added in two
 * paths:
 *   1. Optimistically when the user POSTs (status: "queued", local-only id)
 *   2. Authoritatively when the engine echoes the SSE `user.interject`
 *      event (status: "queued" first, then "applied" once drained into a
 *      round). Server records replace optimistic ones by `id`.
 *
 * Invariants:
 *   • Records are ordered by `receivedAt` ascending.
 *   • A given `id` exists at most once.
 *   • `applied` records carry a non-null `appliedAtRound`.
 */
import type { StateCreator } from "zustand";
import type { UserInterjection } from "../../domain/types";
import type { DebateStore } from "../debateStore";

export interface InterjectionSlice {
  interjections: UserInterjection[];
  interjectionPending: boolean;
  interjectionError: string | null;

  /** Engine echoed a record — upsert by id, sort by receivedAt. */
  ingestServerInterjection(rec: UserInterjection): void;
  /** Local optimistic insert before the server echoes back. */
  optimisticInsertInterjection(rec: UserInterjection): void;
  /**
   * Atomically rename a local-prefixed optimistic id to the server-issued
   * canonical id so the next SSE echo lands on the same row (no UI
   * duplication). No-op when `oldId` doesn't exist.
   */
  replaceInterjectionId(oldId: string, newId: string): void;
  /** Remove an interjection by id (used to rollback a failed optimistic insert). */
  removeInterjection(id: string): void;
  setInterjectionPending(pending: boolean): void;
  setInterjectionError(err: string | null): void;
  resetInterjections(): void;
}

export type InterjectionSliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  InterjectionSlice
>;

export const createInterjectionSlice: InterjectionSliceCreator = (set) => ({
  interjections: [],
  interjectionPending: false,
  interjectionError: null,

  ingestServerInterjection: (rec) => {
    set((state) => {
      const idx = state.interjections.findIndex((m) => m.id === rec.id);
      if (idx === -1) {
        state.interjections.push(rec);
      } else {
        // Server is the source-of-truth for status / appliedAtRound.
        state.interjections[idx] = { ...state.interjections[idx]!, ...rec };
      }
      state.interjections.sort((a, b) => a.receivedAt - b.receivedAt);
    });
  },

  optimisticInsertInterjection: (rec) => {
    set((state) => {
      if (state.interjections.some((m) => m.id === rec.id)) return;
      state.interjections.push(rec);
      state.interjections.sort((a, b) => a.receivedAt - b.receivedAt);
    });
  },

  replaceInterjectionId: (oldId, newId) => {
    set((state) => {
      const idx = state.interjections.findIndex((m) => m.id === oldId);
      if (idx === -1) return;
      // If a row already exists at newId (rare race) drop the local
      // copy entirely so we don't keep a phantom duplicate.
      if (state.interjections.some((m) => m.id === newId && m.id !== oldId)) {
        state.interjections.splice(idx, 1);
        return;
      }
      state.interjections[idx]!.id = newId;
    });
  },

  removeInterjection: (id) => {
    set((state) => {
      const idx = state.interjections.findIndex((m) => m.id === id);
      if (idx !== -1) state.interjections.splice(idx, 1);
    });
  },

  setInterjectionPending: (pending) => {
    set((state) => {
      state.interjectionPending = pending;
    });
  },

  setInterjectionError: (err) => {
    set((state) => {
      state.interjectionError = err;
    });
  },

  resetInterjections: () => {
    set((state) => {
      state.interjections = [];
      state.interjectionPending = false;
      state.interjectionError = null;
    });
  },
});
